<template>
  <div class="login-form" layout layout-align="center-center">
    <form @submit.prevent="submit($v)">
      <el-card>
        <div class="el-card-header">
          <div class="logo"><img src="@/client/assets/images/atelu_logo.svg" /></div>
        </div>
        <div v-if="!isTwoFactor">
          <div>
            <div class="invite-title" v-if="invitation && invitation.code">
              {{ invitation.group.name }}に参加するために、ログインしてください。
            </div>
            <div class="message message-error mt-x1 mb-x1" v-if="isLoginAttemptsExceeded">
              ログインの試行に続けて失敗したため、アカウントがロックされました。数時間後に再度お試しください。
            </div>
            <div class="input-container">
              <el-input
                :class="{
                  error: $v.email.required && $v.email.$error,
                  success: $v.email.required && !$v.email.$invalid
                }"
                placeholder="メールアドレス"
                :maxlength="100"
                :disabled="invitation && invitation.code ? true : false"
                type="text"
                v-model="email"
                @blur="$v.email.$touch"
                tabindex="1"
              >
                <template slot="prepend"><i class="mdi mdi-email"></i></template>
              </el-input>
              <div class="invalid" v-if="!$v.email.required && $v.email.$error">メールアドレスを入力してください。</div>
              <div class="invalid" v-if="!$v.email.email && $v.email.$error">
                メールアドレスの形式が正しくありません。
              </div>
            </div>
            <div class="input-container password">
              <el-input
                :class="{ success: $v.password.required && !$v.password.$invalid }"
                placeholder="パスワード"
                :maxlength="100"
                type="password"
                v-model="password"
                @blur="$v.password.$touch"
                tabindex="2"
              >
                <template slot="prepend"><i class="mdi mdi-key-variant"></i></template>
              </el-input>
              <div class="invalid" v-if="!$v.password.required && $v.password.$error">
                パスワードを入力してください。
              </div>
              <div class="mt-x1">
                <span class="small link" @click="$router.push('/password?mode=requestNew')"
                  >パスワードをお忘れですか?</span
                >
              </div>
            </div>
          </div>
          <div class="el-card-action mt-x4">
            <el-button type="primary" native-type="submit" :disabled="$v.$invalid === true || isLoginAttemptsExceeded"
              >ログイン</el-button
            >
          </div>
          <div class="el-card-action" v-if="!invitation || !invitation.code">
            <el-button class="el-button--facebook" @click="fbLoginClick"
              ><i class="mdi mdi-facebook-box"></i>Facebookログイン</el-button
            >
          </div>
          <div class="mt-x1">
            <a class="small link" href="https://www.comnico.jp/products/atelu/jp/tos" target="_blank">利用規約</a
            ><span class="small text"> と </span
            ><a class="small link" href="https://www.comnico.jp/privacypolicy" target="_blank">個人情報保護方針</a
            ><span class="small text"> に同意のうえ、ログインしてください。</span>
          </div>
        </div>
        <two-factor-form v-else :user-id="userId" @login="loginTwoFactor" @back="back"></two-factor-form>
      </el-card>
    </form>
  </div>
</template>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.login-form {
  height: auto;
}

form {
  width: 100%;
  max-width: $spacing-unit * 62;
}

.el-card {
  width: 100%;
  padding: $spacing-unit * 2;
}

.el-card-header {
  padding: $spacing-unit * 3;

  .logo {
    text-align: center;
  }
}

.invite-title {
  text-align: center;
}

.el-card-action {
  display: block;
  margin: $spacing-unit * 2 0;

  button {
    width: 100%;
  }
}

.input-container {
  width: 100%;
  margin: $spacing-unit * 3 0 0;

  .invalid {
    display: block;
    margin-top: $spacing-unit;
    font-size: $font-small-size;
    color: $danger-color;
  }
}

.small {
  margin-top: $spacing-unit;
}
</style>
<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { email, required } from 'vuelidate/lib/validators'

import api from '@/client/core/api'
import LocalStorage from '@/client/core/local_storage'
import TwoFactorForm from '@/client/features/login/two_factor_form'
import { getLoginStatus, login } from '@/client/utils/facebook_sdk'
import { PostMeSession } from '@/typings/api/me_session.type'
import { PostTwoFactorAuthData } from '@/typings/two_factor'

type Invitation = {
  code: string
  email: string
  isGuest: boolean
  group: {
    id: number
    name: string
  }
}

export default defineComponent({
  components: {
    TwoFactorForm
  },
  props: {
    invitation: Object as PropType<Invitation>
  },
  data() {
    return {
      email: '',
      password: '',
      userId: 0,
      facebook: {} as { userId?: string; accessToken?: string },
      isLoginAttemptsExceeded: false,
      isTwoFactor: false
    }
  },
  created() {
    if (this.invitation && this.invitation.code) {
      this.email = this.invitation.email
    }
  },
  methods: {
    /**
     * ログインサブミット
     * @return {void}
     */
    submit: async function ($v: any) {
      // validations
      $v.$touch()
      if ($v.$invalid === true) {
        return
      }
      // ログインをする
      this.login('default')
    },

    /**
     * FBログインボタンをクリックするイベント
     * @return {void}
     */
    async fbLoginClick() {
      let response = await getLoginStatus()

      if (response.status !== 'connected') {
        response = await login()
      }

      if (response.status !== 'connected') {
        this.openFailNotification('AUTH_FAIL', 'facebook')
        return
      }

      this.facebook = {
        userId: response.authResponse.userID,
        accessToken: response.authResponse.accessToken
      }

      // ログインをする
      return this.login('facebook')
    },

    /**
     * ログイン
     * @param {('default' | 'facebook')} authType 認証タイプ
     * @return {void}
     */
    async login(authType: 'default' | 'facebook') {
      let params = {}
      if (authType === 'default') {
        params = {
          authType,
          email: this.email,
          password: this.password
        }
      } else if (authType === 'facebook') {
        params = {
          email: this.email,
          authType,
          fbUserId: this.facebook.userId,
          fbUserToken: this.facebook.accessToken
        }
      } else {
        return
      }

      try {
        const result: PostMeSession = await api.post('/me/session', params)
        if (result.error) {
          if (result.error.type === 'LOGIN_ATTEMPTS_EXCEEDED') {
            this.isLoginAttemptsExceeded = true
            return
          }

          this.openFailNotification(result.error.type, authType)
          return
        }
        // ログインが成功した場合は、キャンペーン画面に移動する
        if (result.data) {
          if (result.data.is_two_factor) {
            this.userId = result.data.id
            this.isTwoFactor = true
            return
          }

          await this.authorize(result.data)

          return
        }
        // 失敗した場合はエラーを表示する
        this.openFailNotification('REQUEST_FAIL')
      } catch {
        // 失敗した場合はエラーを表示する
        this.openFailNotification('REQUEST_FAIL')
      }
    },

    /**
     * ログイン2要素
     */
    async loginTwoFactor(data: PostTwoFactorAuthData) {
      if (!data) {
        return
      }

      await this.authorize(data)
    },

    /**
     * キャンペーンに移動
     */
    async authorize(data: any) {
      // 招待情報がある場合は、招待処理をする
      if (data && this.invitation && this.invitation.code) {
        await this.invitationAccept(data.id, this.invitation)
        this.$router.push('/')
        return
      }

      let originUrl = String(this.$route.query.originUrl || '/')
      originUrl += this.$route.hash || ''
      location.href = originUrl
    },

    /**
     * ログインフォームに戻る
     */
    back() {
      this.password = ''
      this.$v.$reset()
      this.isTwoFactor = false
    },

    /**
     * 招待処理をする
     * @param {number} userId ユーザーID
     * @param {Invitation} invitation 招待情報
     * @return {void}
     */
    async invitationAccept(userId: number, invitation: Invitation) {
      const params = {
        code: invitation.code,
        type: 'exists_user',
        isGuest: invitation.isGuest
      }
      const result = await api.post('/invitations/accept', params)
      if (result.error && result.error.type === 'GROUP_MAX_USER_COUNT_OVER') {
        this.$notify({
          title: 'これ以上ユーザーを追加できません。',
          message: 'グループの登録可能ユーザー数を超えています。',
          customClass: 'danger',
          duration: 10000
        })
        this.$router.push('/')
        return
      }
      if (result.error) {
        this.$notify({
          title: 'グループ参加処理に失敗しました。',
          message: '恐れ入りますが、時間をおいて再度お試しください。',
          customClass: 'danger',
          duration: 10000
        })
        this.$router.push('/')
        return
      }

      if (result.data) {
        this.$notify({
          title: `${invitation.group.name} グループに参加しました。`,
          message: '',
          customClass: 'success',
          duration: 5000
        })
        // ローカルストレージに現在のグループを保存する
        let currentGroupLocal = LocalStorage.getCurrentGroup()
        if (!currentGroupLocal) {
          currentGroupLocal = {}
        }
        currentGroupLocal[userId] = invitation.group.id
        LocalStorage.setCurrentGroup(currentGroupLocal)
        // 契約情報入力画面に移動する
        this.$router.push('/settings/group_billing')
        return
      }

      // エラーを表示する
      this.$notify({
        title: 'グループ参加処理に失敗しました。',
        message: '',
        customClass: 'danger',
        duration: 10000
      })
    },

    /**
     * 失敗メッセージを表示する
     * @param {string} errorType エラータイプ
     * @param {('default' | 'facebook')} authType 認証タイプ
     * @return {void}
     */
    openFailNotification(errorType: string, authType?: 'default' | 'facebook') {
      let title = ''
      let message = ''
      if (errorType === 'REQUEST_FAIL') {
        title = 'ログインに失敗しました。'
        message = 'エラーが発生しました。恐れ入りますが時間を置いて再ログインしてください。'
      } else {
        if (authType === 'default') {
          title = 'ログインに失敗しました。'
          message = '正しいメールアドレスとパスワードの組み合わせを入力してください。'
        } else if (authType === 'facebook') {
          if (errorType === 'NOT_CONNECTED') {
            title = 'Facebookアカウントが連携されていません。'
          } else {
            title = 'Facebook認証に失敗しました。'
          }
        }
      }
      this.$notify({
        title,
        message,
        customClass: 'danger',
        duration: 10000
      })
    }
  },
  validations: {
    email: {
      required,
      email
    },
    password: {
      required
    }
  }
})
</script>
